import type {
  ApiResponse,
  AuthResponse,
  InvitationPublic,
  LoginRequest,
  OrganizationPublic,
  PaginatedResponse,
  PasswordResetConfirm,
  RegisterRequest,
  SessionInfo,
  UserPublic,
} from '@garden-platform/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();
  return data;
}

// Auth endpoints
export async function login(credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> {
  return fetchApi('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export async function register(data: RegisterRequest): Promise<ApiResponse<AuthResponse>> {
  return fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function logout(): Promise<ApiResponse<void>> {
  return fetchApi('/auth/logout', { method: 'POST' });
}

export async function getCurrentUser(): Promise<ApiResponse<AuthResponse>> {
  return fetchApi('/auth/me');
}

export async function verifyEmail(token: string): Promise<ApiResponse<void>> {
  return fetchApi(`/auth/verify/${token}`);
}

export async function resendVerification(email?: string): Promise<ApiResponse<void>> {
  return fetchApi('/auth/send-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function forgotPassword(email: string): Promise<ApiResponse<void>> {
  return fetchApi('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(data: PasswordResetConfirm): Promise<ApiResponse<void>> {
  return fetchApi('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// OAuth URLs
export function getGoogleAuthUrl(): string {
  return `${API_URL}/auth/google`;
}

export function getMicrosoftAuthUrl(): string {
  return `${API_URL}/auth/microsoft`;
}

// Organization endpoints
export async function getOrganization(): Promise<ApiResponse<OrganizationPublic>> {
  return fetchApi('/organizations');
}

export async function updateOrganization(data: {
  name: string;
}): Promise<ApiResponse<OrganizationPublic>> {
  return fetchApi('/organizations', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getMembers(
  page = 1,
  limit = 20,
): Promise<ApiResponse<PaginatedResponse<UserPublic>>> {
  return fetchApi(`/organizations/members?page=${page}&limit=${limit}`);
}

export async function changeMemberRole(
  memberId: string,
  role: 'admin' | 'member',
): Promise<ApiResponse<UserPublic>> {
  return fetchApi(`/organizations/members/${memberId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function transferAdmin(newAdminId: string): Promise<ApiResponse<void>> {
  return fetchApi('/organizations/transfer-admin', {
    method: 'POST',
    body: JSON.stringify({ newAdminId }),
  });
}

export async function removeMember(memberId: string): Promise<ApiResponse<void>> {
  return fetchApi(`/organizations/members/${memberId}`, { method: 'DELETE' });
}

// Invitation endpoints
export async function getInvitations(
  page = 1,
  limit = 20,
  status?: string,
): Promise<ApiResponse<PaginatedResponse<InvitationPublic>>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.append('status', status);
  return fetchApi(`/invitations?${params}`);
}

export async function createInvitation(
  email: string,
  role?: 'admin' | 'member',
): Promise<ApiResponse<InvitationPublic>> {
  return fetchApi('/invitations', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function validateInvitation(
  token: string,
): Promise<ApiResponse<{ email: string; organizationName: string; expiresAt: Date }>> {
  return fetchApi(`/invitations/${token}`);
}

export async function acceptInvitation(
  token: string,
  data: { password: string; name?: string },
): Promise<ApiResponse<AuthResponse>> {
  return fetchApi(`/invitations/${token}/accept`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function declineInvitation(token: string): Promise<ApiResponse<void>> {
  return fetchApi(`/invitations/${token}/decline`, { method: 'POST' });
}

export async function revokeInvitation(invitationId: string): Promise<ApiResponse<void>> {
  return fetchApi(`/invitations/${invitationId}`, { method: 'DELETE' });
}

export async function resendInvitation(
  invitationId: string,
): Promise<ApiResponse<InvitationPublic>> {
  return fetchApi(`/invitations/${invitationId}/resend`, { method: 'POST' });
}

// Admin session endpoints
export async function getSessions(
  page = 1,
  limit = 20,
  status?: 'valid' | 'invalid',
  userId?: string,
): Promise<ApiResponse<PaginatedResponse<SessionInfo>>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.append('status', status);
  if (userId) params.append('userId', userId);
  return fetchApi(`/admin/sessions?${params}`);
}

export async function getSessionStats(): Promise<
  ApiResponse<{
    activeSessions: number;
    totalSessions: number;
    activeUsers: number;
    totalMembers: number;
  }>
> {
  return fetchApi('/admin/sessions/stats');
}

export async function invalidateSession(sessionId: string): Promise<ApiResponse<void>> {
  return fetchApi(`/admin/sessions/${sessionId}/invalidate`, { method: 'POST' });
}

export async function invalidateUserSessions(userId: string): Promise<ApiResponse<void>> {
  return fetchApi(`/admin/users/${userId}/invalidate-sessions`, { method: 'POST' });
}

export async function getUserSessions(userId: string): Promise<ApiResponse<SessionInfo[]>> {
  return fetchApi(`/admin/users/${userId}/sessions`);
}
